import React, { useState, useEffect } from "react";
import HoldingsSection from "./holding_section";
import GoldCard from "./metal_card";
import DiamondPointsCard from "./diamond_points_section";
import { toast } from "react-toastify";
import { FaCrown, FaFire, FaGem, FaHeart } from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchThemeRequest,
  createThemeRequest,
  clearThemeMessages,
} from "../../../redux/AppTheme/AppThemeSlice";
import { useNavigate } from "react-router-dom";
import { canShowPermission } from "../Permissions/permissionObject";

const colorConfigs = [
  {
    key: "buttonColor",
    label: "Button Color",
    default: "#250164",
    description: "Color for buttons and interactive elements.",
  },
  {
    key: "containerBorderColor",
    label: "Container Border Color",
    default: "#E0E0E0",
    description: "Color for container borders.",
  },
  {
    key: "appBarColor",
    label: "App Bar Color",
    default: "#250164",
    description: "Color for app bar and status bar.",
  },
  {
    key: "appBarTextColor",
    label: "App Bar Text Color",
    default: "#FFFFFF",
    description: "Color for app bar title and action icons.",
  },
  {
    key: "textColor",
    label: "Text Color",
    default: "#000000",
    description: "Color for text in the app.",
  },
  {
    key: "scaffoldTextColor",
    label: "Screen Text Color",
    default: "#000000",
    description: "Color for text shown on the main screen background.",
  },
  {
    key: "svgColor",
    label: "Icons Color",
    default: "#000000",
    description: "Color for icons used in the app.",
  },
  {
    key: "bottomNavBarBackgroundColor",
    label: "Bottom NavBar Background",
    default: "#FFFFFF",
    description: "Background color for bottom navigation bar.",
  },
  {
    key: "bottomNavBarSelectedColor",
    label: "Bottom NavBar Selected",
    default: "#250164",
    description: "Color for selected tab in bottom navigation.",
  },
  {
    key: "bottomNavBarUnselectedColor",
    label: "Bottom NavBar Unselected",
    default: "#666666",
    description: "Color for unselected tabs in bottom navigation.",
  },
  {
    key: "amountOptionColor",
    label: "Amount Option Color",
    default: "#250164",
    description: "Color for amount option buttons like 101, 501, 1001, 5001.",
  },
  {
    key: "splashColor",
    label: "Splash Color",
    default: "#250164",
    description: "Background color used on splash/loading screen.",
  },
  {
    key: "otpScreenBackgroundColor",
    label: "OTP Screen Background Color",
    default: "#FFFFFF",
    description: "Background color used on OTP verification screen.",
  },
];

const premiumThemePresets = [
  {
    name: "Sapphire Blue",
    themeType: "theme_one",
    preview: ["#1C5DB1", "#00203F"],
    icon: FaGem,
    iconBg: "#1C5DB1",
    iconColor: "#FFFFFF",
    values: {
      scaffoldBackgroundColor: "#1C5DB1",
      scaffoldGradientColors: ["#1C5DB1", "#00203F"],
      buttonColor: "#00203F",
      containerColor: "#1C5DB1",
      containerBorderColor: "#FFFFFF",
      appBarColor: "#1C5DB1",
      appBarTextColor: "#1C5DB1",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#FFFFFF",
      bottomNavBarBackgroundColor: "#00203F",
      bottomNavBarSelectedColor: "#1C5DB1",
      bottomNavBarUnselectedColor: "#F6F6F6",
      amountOptionColor: "#00203F",
      splashColor: "#FFFFFF",
      otpScreenBackgroundColor: "#1C5DB1",
      smallContainerGradientColors: ["#1C5DB1", "#00203F"],
      mediumContainerGradientColors: ["#1C5DB1", "#00203F"],
      bigContainerGradientColors: ["#1C5DB1", "#00203F"],
    },
  },
  {
    name: "Ember Black",
    themeType: "theme_two",
    preview: ["#000000", "#FF4E1A"],
    icon: FaFire,
    iconBg: "#FF4E1A",
    iconColor: "#FFFFFF",
    values: {
      // Whole page bg: #000000 | Icons bg: #FF4E1A
      // Buttons & plan-goals gradient: #FE905D | Icons colour: #FFFFFF
      scaffoldBackgroundColor: "#000000",
      scaffoldGradientColors: ["#000000", "#000000"],
      buttonColor: "#FE905D",
      containerColor: "#000000",
      containerBorderColor: "#FF4E1A",
      appBarColor: "#000000",
      appBarTextColor: "#FFFFFF",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#FFFFFF",
      bottomNavBarBackgroundColor: "#000000",
      bottomNavBarSelectedColor: "#FF4E1A",
      bottomNavBarUnselectedColor: "#FE905D",
      amountOptionColor: "#FF4E1A",
      splashColor: "#000000",
      otpScreenBackgroundColor: "#000000",
      smallContainerGradientColors: ["#000000", "#000000"],
      mediumContainerGradientColors: ["#000000", "#000000"],
      bigContainerGradientColors: ["#000000", "#000000"],
    },
  },
  {
    name: "Rose Blush",
    themeType: "theme_three",
    preview: ["#FFFFFF", "#FEA8CA"],
    icon: FaHeart,
    iconBg: "#FEA8CA",
    iconColor: "#2C0F17",
    values: {
      // Icons colour: #2C0F17 | Carousel bg: #401F27 | Carousel colour: #FFC4C5
      // Plan-goals icons bg bar: #FEA8CA | Icons bg bar & page bg: #FFFFFF
      scaffoldBackgroundColor: "#FFFFFF",
      scaffoldGradientColors: ["#FFFFFF", "#FFFFFF"],
      buttonColor: "#FEA8CA",
      containerColor: "#401F27",
      containerBorderColor: "#FFC4C5",
      appBarColor: "#FFFFFF",
      appBarTextColor: "#2C0F17",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#2C0F17",
      svgColor: "#2C0F17",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#FEA8CA",
      bottomNavBarUnselectedColor: "#401F27",
      amountOptionColor: "#FEA8CA",
      splashColor: "#FEA8CA",
      otpScreenBackgroundColor: "#FFFFFF",
      smallContainerGradientColors: ["#FEA8CA", "#FFFFFF"],
      mediumContainerGradientColors: ["#401F27", "#FFC4C5"],
      bigContainerGradientColors: ["#FFC4C5", "#FEA8CA"],
    },
  },
];

const themePresets = [
  {
    name: "Default Theme",
    preview: ["#FFFFFF", "#FFFFFF"],
    values: {
      scaffoldBackgroundColor: "#250164",
      scaffoldGradientColors: ["#FFFFFF", "#FFFFFF"],
      buttonColor: "#44745E",
      containerColor: "#250164",
      containerBorderColor: "#33FFFFFF",
      appBarColor: "#FAFEFF",
      appBarTextColor: "#FFFFFF",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#ECE4E4",
      bottomNavBarBackgroundColor: "#011D31",
      bottomNavBarSelectedColor: "#00203F",
      bottomNavBarUnselectedColor: "#F6F6F6",
      amountOptionColor: "#44745E",
      splashColor: "#44745E",
      otpScreenBackgroundColor: "#FFFFFF",
      smallContainerGradientColors: ["#00203F", "#00203F"],
      mediumContainerGradientColors: ["#00203F", "#00203F"],
      bigContainerGradientColors: ["#00203F", "#00203F"],
    },
  },
  {
    name: "Royal Purple",
    preview: ["#F5F3FF", "#EDE9FE"],
    values: {
      scaffoldBackgroundColor: "#F5F3FF",
      scaffoldGradientColors: ["#F5F3FF", "#EDE9FE"],
      buttonColor: "#6D28D9",
      containerColor: "#FAF5FF",
      containerBorderColor: "#C4B5FD",
      appBarColor: "#4C1D95",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#3B0764",
      svgColor: "#5B21B6",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#6D28D9",
      bottomNavBarUnselectedColor: "#7C6BB2",
      amountOptionColor: "#A78BFA",
      smallContainerGradientColors: ["#C4B5FD", "#DDD6FE"],
      mediumContainerGradientColors: ["#A78BFA", "#C4B5FD"],
      bigContainerGradientColors: ["#8B5CF6", "#A78BFA"],
    },
  },
  {
    name: "Ocean Blue",
    preview: ["#E0F2FE", "#DBEAFE"],
    values: {
      scaffoldBackgroundColor: "#E0F2FE",
      scaffoldGradientColors: ["#E0F2FE", "#DBEAFE"],
      buttonColor: "#0369A1",
      containerColor: "#F0F9FF",
      containerBorderColor: "#7DD3FC",
      appBarColor: "#0C4A6E",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#0F172A",
      svgColor: "#075985",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#0369A1",
      bottomNavBarUnselectedColor: "#64748B",
      amountOptionColor: "#38BDF8",
      smallContainerGradientColors: ["#38BDF8", "#7DD3FC"],
      mediumContainerGradientColors: ["#0EA5E9", "#38BDF8"],
      bigContainerGradientColors: ["#0284C7", "#0EA5E9"],
    },
  },
  {
    name: "Emerald Fresh",
    preview: ["#ECFDF5", "#D1FAE5"],
    values: {
      scaffoldBackgroundColor: "#ECFDF5",
      scaffoldGradientColors: ["#ECFDF5", "#D1FAE5"],
      buttonColor: "#047857",
      containerColor: "#F0FDF4",
      containerBorderColor: "#86EFAC",
      appBarColor: "#065F46",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#14532D",
      svgColor: "#047857",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#047857",
      bottomNavBarUnselectedColor: "#6B7280",
      amountOptionColor: "#34D399",
      smallContainerGradientColors: ["#34D399", "#6EE7B7"],
      mediumContainerGradientColors: ["#10B981", "#34D399"],
      bigContainerGradientColors: ["#059669", "#10B981"],
    },
  },
  {
    name: "Sunset Warm",
    preview: ["#FFF7ED", "#FFEDD5"],
    values: {
      scaffoldBackgroundColor: "#FFF7ED",
      scaffoldGradientColors: ["#FFF7ED", "#FFEDD5"],
      buttonColor: "#EA580C",
      containerColor: "#FFFBEB",
      containerBorderColor: "#FDBA74",
      appBarColor: "#C2410C",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#7C2D12",
      svgColor: "#C2410C",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#EA580C",
      bottomNavBarUnselectedColor: "#78716C",
      amountOptionColor: "#FB923C",
      smallContainerGradientColors: ["#FB923C", "#FDBA74"],
      mediumContainerGradientColors: ["#F97316", "#FB923C"],
      bigContainerGradientColors: ["#EA580C", "#F97316"],
    },
  },
  {
    name: "Rose Gold",
    preview: ["#FFF1F2", "#FFE4E6"],
    values: {
      scaffoldBackgroundColor: "#FFF1F2",
      scaffoldGradientColors: ["#FFF1F2", "#FFE4E6"],
      buttonColor: "#BE185D",
      containerColor: "#FFF7F9",
      containerBorderColor: "#FDA4AF",
      appBarColor: "#9D174D",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#881337",
      svgColor: "#BE185D",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#BE185D",
      bottomNavBarUnselectedColor: "#71717A",
      amountOptionColor: "#FB7185",
      smallContainerGradientColors: ["#FB7185", "#FDA4AF"],
      mediumContainerGradientColors: ["#F43F5E", "#FB7185"],
      bigContainerGradientColors: ["#E11D48", "#F43F5E"],
    },
  },
  {
    name: "Midnight Dark",
    preview: ["#111827", "#1F2937"],
    values: {
      scaffoldBackgroundColor: "#111827",
      scaffoldGradientColors: ["#111827", "#1F2937"],
      buttonColor: "#6366F1",
      containerColor: "#1F2937",
      containerBorderColor: "#374151",
      appBarColor: "#030712",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#A5B4FC",
      bottomNavBarBackgroundColor: "#111827",
      bottomNavBarSelectedColor: "#818CF8",
      bottomNavBarUnselectedColor: "#9CA3AF",
      amountOptionColor: "#374151",
      smallContainerGradientColors: ["#312E81", "#4338CA"],
      mediumContainerGradientColors: ["#3730A3", "#4F46E5"],
      bigContainerGradientColors: ["#4338CA", "#6366F1"],
    },
  },
  {
    name: "Coffee Brown",
    preview: ["#FAF5F0", "#F5E9DC"],
    values: {
      scaffoldBackgroundColor: "#FAF5F0",
      scaffoldGradientColors: ["#FAF5F0", "#F5E9DC"],
      buttonColor: "#6F4E37",
      containerColor: "#FFF7ED",
      containerBorderColor: "#D6BFA6",
      appBarColor: "#6F4E37",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#4B2E1A",
      svgColor: "#6F4E37",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#8B5E3C",
      bottomNavBarUnselectedColor: "#78716C",
      amountOptionColor: "#B08968",
      smallContainerGradientColors: ["#B08968", "#DDB892"],
      mediumContainerGradientColors: ["#A47148", "#C89F7A"],
      bigContainerGradientColors: ["#8B5E3C", "#A47148"],
    },
  },
  {
    name: "Candy Pop",
    preview: ["#FDF4FF", "#FAE8FF"],
    values: {
      scaffoldBackgroundColor: "#FDF4FF",
      scaffoldGradientColors: ["#FDF4FF", "#FAE8FF"],
      buttonColor: "#DB2777",
      containerColor: "#FFF1F8",
      containerBorderColor: "#F9A8D4",
      appBarColor: "#BE185D",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#701A75",
      svgColor: "#BE185D",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#DB2777",
      bottomNavBarUnselectedColor: "#71717A",
      amountOptionColor: "#E879F9",
      smallContainerGradientColors: ["#E879F9", "#F0ABFC"],
      mediumContainerGradientColors: ["#D946EF", "#E879F9"],
      bigContainerGradientColors: ["#C026D3", "#D946EF"],
    },
  },
  {
    name: "Neon Night",
    preview: ["#0B1120", "#172554"],
    values: {
      scaffoldBackgroundColor: "#0B1120",
      scaffoldGradientColors: ["#0B1120", "#172554"],
      buttonColor: "#22D3EE",
      containerColor: "#1E293B",
      containerBorderColor: "#334155",
      appBarColor: "#020617",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#67E8F9",
      bottomNavBarBackgroundColor: "#0F172A",
      bottomNavBarSelectedColor: "#22D3EE",
      bottomNavBarUnselectedColor: "#94A3B8",
      amountOptionColor: "#334155",
      smallContainerGradientColors: ["#0EA5E9", "#22D3EE"],
      mediumContainerGradientColors: ["#0284C7", "#06B6D4"],
      bigContainerGradientColors: ["#0369A1", "#0EA5E9"],
    },
  },
  {
    name: "Charcoal Blue",
    preview: ["#0F172A", "#1E293B"],
    values: {
      scaffoldBackgroundColor: "#0F172A",
      scaffoldGradientColors: ["#0F172A", "#1E293B"],
      buttonColor: "#3B82F6",
      containerColor: "#1E293B",
      containerBorderColor: "#334155",
      appBarColor: "#020617",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#60A5FA",
      bottomNavBarBackgroundColor: "#111827",
      bottomNavBarSelectedColor: "#3B82F6",
      bottomNavBarUnselectedColor: "#94A3B8",
      amountOptionColor: "#334155",
      smallContainerGradientColors: ["#1D4ED8", "#2563EB"],
      mediumContainerGradientColors: ["#2563EB", "#3B82F6"],
      bigContainerGradientColors: ["#1E40AF", "#2563EB"],
    },
  },
  {
    name: "Dark Emerald",
    preview: ["#052E2B", "#064E3B"],
    values: {
      scaffoldBackgroundColor: "#052E2B",
      scaffoldGradientColors: ["#052E2B", "#064E3B"],
      buttonColor: "#10B981",
      containerColor: "#0B2F2A",
      containerBorderColor: "#1F4E46",
      appBarColor: "#022C22",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#34D399",
      bottomNavBarBackgroundColor: "#052E2B",
      bottomNavBarSelectedColor: "#10B981",
      bottomNavBarUnselectedColor: "#94A3B8",
      amountOptionColor: "#1F4E46",
      smallContainerGradientColors: ["#047857", "#059669"],
      mediumContainerGradientColors: ["#059669", "#10B981"],
      bigContainerGradientColors: ["#065F46", "#059669"],
    },
  },
  {
    name: "Obsidian Purple",
    preview: ["#140A2E", "#1F123A"],
    values: {
      scaffoldBackgroundColor: "#140A2E",
      scaffoldGradientColors: ["#140A2E", "#1F123A"],
      buttonColor: "#8B5CF6",
      containerColor: "#22143F",
      containerBorderColor: "#3A2A5D",
      appBarColor: "#100726",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#A78BFA",
      bottomNavBarBackgroundColor: "#1A1033",
      bottomNavBarSelectedColor: "#8B5CF6",
      bottomNavBarUnselectedColor: "#A1A1AA",
      amountOptionColor: "#3A2A5D",
      smallContainerGradientColors: ["#6D28D9", "#7C3AED"],
      mediumContainerGradientColors: ["#7C3AED", "#8B5CF6"],
      bigContainerGradientColors: ["#5B21B6", "#6D28D9"],
    },
  },
  {
    name: "Crimson Night",
    preview: ["#1F0A0A", "#3A1010"],
    values: {
      scaffoldBackgroundColor: "#1F0A0A",
      scaffoldGradientColors: ["#1F0A0A", "#3A1010"],
      buttonColor: "#DC2626",
      containerColor: "#2B0F0F",
      containerBorderColor: "#4A1A1A",
      appBarColor: "#140707",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#FFFFFF",
      svgColor: "#F87171",
      bottomNavBarBackgroundColor: "#240B0B",
      bottomNavBarSelectedColor: "#DC2626",
      bottomNavBarUnselectedColor: "#A1A1AA",
      amountOptionColor: "#4A1A1A",
      smallContainerGradientColors: ["#B91C1C", "#DC2626"],
      mediumContainerGradientColors: ["#DC2626", "#EF4444"],
      bigContainerGradientColors: ["#991B1B", "#B91C1C"],
    },
  },
  {
    name: "Ruby Light",
    preview: ["#FFF1F2", "#FFE4E6"],
    values: {
      scaffoldBackgroundColor: "#FFF1F2",
      scaffoldGradientColors: ["#FFF1F2", "#FFE4E6"],
      buttonColor: "#E11D48",
      containerColor: "#FFF5F6",
      containerBorderColor: "#FDA4AF",
      appBarColor: "#BE123C",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#881337",
      svgColor: "#FB7185",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#E11D48",
      bottomNavBarUnselectedColor: "#71717A",
      amountOptionColor: "#FB7185",
      smallContainerGradientColors: ["#FDA4AF", "#FECDD3"],
      mediumContainerGradientColors: ["#FB7185", "#FDA4AF"],
      bigContainerGradientColors: ["#F43F5E", "#FB7185"],
    },
  },
  {
    name: "Sky Light",
    preview: ["#F0F9FF", "#E0F2FE"],
    values: {
      scaffoldBackgroundColor: "#F0F9FF",
      scaffoldGradientColors: ["#F0F9FF", "#E0F2FE"],
      buttonColor: "#0284C7",
      containerColor: "#F8FCFF",
      containerBorderColor: "#BAE6FD",
      appBarColor: "#0369A1",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#0C4A6E",
      svgColor: "#38BDF8",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#0284C7",
      bottomNavBarUnselectedColor: "#64748B",
      amountOptionColor: "#7DD3FC",
      smallContainerGradientColors: ["#BAE6FD", "#E0F2FE"],
      mediumContainerGradientColors: ["#7DD3FC", "#BAE6FD"],
      bigContainerGradientColors: ["#38BDF8", "#7DD3FC"],
    },
  },
  {
    name: "Mint Light",
    preview: ["#F0FDF4", "#DCFCE7"],
    values: {
      scaffoldBackgroundColor: "#F0FDF4",
      scaffoldGradientColors: ["#F0FDF4", "#DCFCE7"],
      buttonColor: "#059669",
      containerColor: "#F7FEFA",
      containerBorderColor: "#BBF7D0",
      appBarColor: "#047857",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#14532D",
      svgColor: "#34D399",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#059669",
      bottomNavBarUnselectedColor: "#6B7280",
      amountOptionColor: "#86EFAC",
      smallContainerGradientColors: ["#BBF7D0", "#DCFCE7"],
      mediumContainerGradientColors: ["#86EFAC", "#BBF7D0"],
      bigContainerGradientColors: ["#4ADE80", "#86EFAC"],
    },
  },
  {
    name: "Lavender Light",
    preview: ["#FAF5FF", "#F3E8FF"],
    values: {
      scaffoldBackgroundColor: "#FAF5FF",
      scaffoldGradientColors: ["#FAF5FF", "#F3E8FF"],
      buttonColor: "#7C3AED",
      containerColor: "#FCF8FF",
      containerBorderColor: "#DDD6FE",
      appBarColor: "#6D28D9",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#4C1D95",
      svgColor: "#A78BFA",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#7C3AED",
      bottomNavBarUnselectedColor: "#7C6BB2",
      amountOptionColor: "#C4B5FD",
      smallContainerGradientColors: ["#DDD6FE", "#F3E8FF"],
      mediumContainerGradientColors: ["#C4B5FD", "#DDD6FE"],
      bigContainerGradientColors: ["#A78BFA", "#C4B5FD"],
    },
  },
  {
    name: "Peach Light",
    preview: ["#FFF7ED", "#FFEDD5"],
    values: {
      scaffoldBackgroundColor: "#FFF7ED",
      scaffoldGradientColors: ["#FFF7ED", "#FFEDD5"],
      buttonColor: "#EA580C",
      containerColor: "#FFFBF5",
      containerBorderColor: "#FED7AA",
      appBarColor: "#C2410C",
      textColor: "#FFFFFF",
      scaffoldTextColor: "#7C2D12",
      svgColor: "#FB923C",
      bottomNavBarBackgroundColor: "#FFFFFF",
      bottomNavBarSelectedColor: "#EA580C",
      bottomNavBarUnselectedColor: "#78716C",
      amountOptionColor: "#FDBA74",
      smallContainerGradientColors: ["#FED7AA", "#FFEDD5"],
      mediumContainerGradientColors: ["#FDBA74", "#FED7AA"],
      bigContainerGradientColors: ["#FB923C", "#FDBA74"],
    },
  },
];

const defaultThemeValues = {
  scaffoldBackgroundColor: "#250164",
  scaffoldGradientColors: ["#FFFFFF", "#FFFFFF"],
  buttonColor: "#44745E",
  containerColor: "#250164",
  containerBorderColor: "#33FFFFFF",
  appBarColor: "#FAFEFF",
  appBarTextColor: "#FFFFFF",
  textColor: "#FFFFFF",
  scaffoldTextColor: "#FFFFFF",
  svgColor: "#ECE4E4",
  bottomNavBarBackgroundColor: "#011D31",
  bottomNavBarSelectedColor: "#00203F",
  bottomNavBarUnselectedColor: "#F6F6F6",
  amountOptionColor: "#44745E",
  splashColor: "#44745E",
  otpScreenBackgroundColor: "#FFFFFF",
  smallContainerGradientColors: ["#00203F", "#00203F"],
  mediumContainerGradientColors: ["#00203F", "#00203F"],
  bigContainerGradientColors: ["#00203F", "#00203F"],
};

export default function SimpleThemeEditor() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const themeState = useSelector((state) => state.theme);
  useEffect(() => {
    dispatch(fetchThemeRequest());
  }, [dispatch]);

  const goToThemeHistory = () => {
    navigate("/AppThemeHistory");
  };

  useEffect(() => {
    const list = themeState?.theme;
    if (Array.isArray(list) && list.length > 0) {
      const t0 = list[0];
      
      // Helper to parse gradient colors - support both array and start/end format
      const parseGradientColors = (startKey, endKey, defaultColors) => {
        if (Array.isArray(t0[`${startKey.replace('StartColor', '')}GradientColors`])) {
          return t0[`${startKey.replace('StartColor', '')}GradientColors`];
        }
        if (t0[startKey] && t0[endKey]) {
          return [t0[startKey], t0[endKey]];
        }
        return defaultColors;
      };
      
      setTheme({
        scaffoldBackgroundColor:
          t0.scaffoldBackgroundColor || t0.scaffoldColor || "#FFFFFF",
        scaffoldGradientColors: parseGradientColors(
          "scaffoldStartColor",
          "scaffoldEndColor",
          t0.scaffoldGradientColors || ["#FFFFFF", "#F5F5F5"]
        ),
        buttonColor: t0.primaryColor || t0.buttonColor || "#250164",
        containerColor: t0.containerColor || "#F5F5F5",
        containerBorderColor: t0.containerBorder || t0.containerBorderColor || "#E0E0E0",
        appBarColor: t0.appBarColor || "#250164",
        appBarTextColor: t0.appBarTextColor || t0.textColor || "#FFFFFF",
        textColor: t0.textColor || "#000000",
        scaffoldTextColor: t0.scaffoldTextColor || t0.textColor || "#000000",
        svgColor: t0.svgColor || "#000000",
        bottomNavBarBackgroundColor: t0.bottomNavBarBackgroundColor || "#FFFFFF",
        bottomNavBarSelectedColor: t0.bottomNavBarSelectedColor || "#250164",
        bottomNavBarUnselectedColor: t0.bottomNavBarUnselectedColor || "#666666",
        amountOptionColor: t0.amountOptionColor || t0.buttonColor || t0.primaryColor || "#250164",
        splashColor: t0.splashColor || t0.buttonColor || t0.primaryColor || "#250164",
        otpScreenBackgroundColor: t0.otpScreenBackgroundColor || t0.otpBackgroundColor || "#FFFFFF",
        smallContainerGradientColors: parseGradientColors(
          "smallContainerStartColor",
          "smallContainerEndColor",
          ["#7A3FFF", "#9E3ED7"]
        ),
        mediumContainerGradientColors: parseGradientColors(
          "mediumContainerStartColor",
          "mediumContainerEndColor",
          ["#5A2FCF", "#8E2EC7"]
        ),
        bigContainerGradientColors: parseGradientColors(
          "bigContainerStartColor",
          "bigContainerEndColor",
          ["#4A1FBF", "#7E1EB7"]
        ),
      });
      const matchedPremium = premiumThemePresets.find(
        (preset) => preset.themeType === t0.themeType
      );
      // themeType only applies to premium themes
      setSelectedThemeType(matchedPremium ? t0.themeType : "");
      setSelectedPresetName(matchedPremium?.name || "");
    } else if (Array.isArray(list) && list.length === 0) {
      setTheme(defaultThemeValues);
      setSelectedPresetName("Default Theme");
      setSelectedThemeType("");
    }
  }, [themeState.theme]);

  const [theme, setTheme] = useState(defaultThemeValues);
  const [selectedPresetName, setSelectedPresetName] = useState("");
  const [selectedThemeType, setSelectedThemeType] = useState("");

  const handleColorChange = (key, value) => {
    setTheme({ ...theme, [key]: value });
    // Manual edits are regular themes — clear premium themeType
    setSelectedThemeType("");
    setSelectedPresetName("");
  };

  const handleGradientColorChange = (containerKey, index, value) => {
    const updatedColors = [...theme[containerKey]];
    updatedColors[index] = value;
    setTheme({ ...theme, [containerKey]: updatedColors });
    setSelectedThemeType("");
    setSelectedPresetName("");
  };

  const addGradientColor = (containerKey) => {
    const currentColors = theme[containerKey] || [];
    const newColor = currentColors.length > 0 
      ? currentColors[currentColors.length - 1] 
      : "#FFFFFF";
    setTheme({ 
      ...theme, 
      [containerKey]: [...currentColors, newColor] 
    });
  };

  const removeGradientColor = (containerKey, index) => {
    const currentColors = theme[containerKey] || [];
    if (currentColors.length > 2) {
      const updatedColors = currentColors.filter((_, i) => i !== index);
      setTheme({ ...theme, [containerKey]: updatedColors });
    }
  };

  const isDarkHexColor = (hexColor) => {
    if (!hexColor || typeof hexColor !== "string") return false;
    const hex = hexColor.replace("#", "");
    const normalized = hex.length === 3
      ? hex.split("").map((c) => c + c).join("")
      : hex.slice(0, 6);
    if (normalized.length !== 6) return false;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 160;
  };

  const hexToRgb = (hexColor) => {
    if (!hexColor || typeof hexColor !== "string") return null;
    const hex = hexColor.replace("#", "");
    const normalized = hex.length === 3
      ? hex.split("").map((c) => c + c).join("")
      : hex.slice(0, 6);
    if (normalized.length !== 6) return null;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  };

  const rgbToHex = ({ r, g, b }) =>
    `#${[r, g, b]
      .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()}`;

  const blendWithWhite = (hexColor, whiteRatio = 0.68) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return null;
    const ratio = Math.max(0, Math.min(1, whiteRatio));
    return rgbToHex({
      r: rgb.r * (1 - ratio) + 255 * ratio,
      g: rgb.g * (1 - ratio) + 255 * ratio,
      b: rgb.b * (1 - ratio) + 255 * ratio,
    });
  };

  const getBrightness = (hexColor) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return 255;
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  };

  const resolveLightCommonScreenColor = (presetValues, prevTheme) => {
    const accentColor =
      presetValues.buttonColor ||
      presetValues.bottomNavBarSelectedColor ||
      presetValues.appBarColor ||
      prevTheme.buttonColor;

    const brightFromAccent = blendWithWhite(accentColor, 0.68);

    const candidates = [
      brightFromAccent,
      presetValues.otpScreenBackgroundColor,
      presetValues.splashColor,
      Array.isArray(presetValues.scaffoldGradientColors)
        ? presetValues.scaffoldGradientColors[0]
        : null,
      presetValues.scaffoldBackgroundColor,
      presetValues.containerColor,
      prevTheme.otpScreenBackgroundColor,
      prevTheme.splashColor,
      "#EAF2FF",
    ].filter(Boolean);

    // Pick a light color that is still visible (avoid near-white flat look).
    const firstBrightButNotWashed = candidates.find((color) => {
      const brightness = getBrightness(color);
      return !isDarkHexColor(color) && brightness <= 242;
    });

    return firstBrightButNotWashed || brightFromAccent || "#EAF2FF";
  };

  const applyThemePreset = (preset) => {
    setTheme((prevTheme) => {
      const commonLightColor = resolveLightCommonScreenColor(preset.values, prevTheme);
      return {
        ...prevTheme,
        ...preset.values,
        appBarTextColor:
          preset.values.appBarTextColor || preset.values.textColor || prevTheme.appBarTextColor,
        splashColor: commonLightColor,
        otpScreenBackgroundColor: commonLightColor,
      };
    });
    setSelectedPresetName(preset.name);
    setSelectedThemeType(preset.themeType || "");
    toast.success(`${preset.name} theme applied`);
  };

  const buildGradientString = (colors, direction = "135deg") => {
    if (!colors || colors.length === 0) return `linear-gradient(${direction}, #7A3FFF, #9E3ED7)`;
    if (colors.length === 1) return `linear-gradient(${direction}, ${colors[0]}, ${colors[0]})`;
    
    const colorStops = colors.map((color, index) => {
      const percentage = (index / (colors.length - 1)) * 100;
      return `${color} ${percentage}%`;
    }).join(", ");
    
    return `linear-gradient(${direction}, ${colorStops})`;
  };

  const buildScaffoldGradientString = (colors) => {
    return buildGradientString(colors, "to bottom");
  };

  function buildThemePayload() {
    const isPremiumTheme = ["theme_one", "theme_two", "theme_three"].includes(
      selectedThemeType
    );

    return {
      scaffoldColor: theme.scaffoldBackgroundColor,
      scaffoldGradientColors: theme.scaffoldGradientColors,
      primaryColor: theme.buttonColor,
      containerColor: theme.containerColor,
      containerBorder: theme.containerBorderColor,
      appBarColor: theme.appBarColor,
      appBarTextColor: theme.appBarTextColor,
      textColor: theme.textColor,
      scaffoldTextColor: theme.scaffoldTextColor,
      svgColor: theme.svgColor,
      bottomNavBarBackgroundColor: theme.bottomNavBarBackgroundColor,
      bottomNavBarSelectedColor: theme.bottomNavBarSelectedColor,
      bottomNavBarUnselectedColor: theme.bottomNavBarUnselectedColor,
      amountOptionColor: theme.amountOptionColor,
      splashColor: theme.splashColor,
      otpScreenBackgroundColor: theme.otpScreenBackgroundColor,
      smallContainerGradientColors: theme.smallContainerGradientColors,
      mediumContainerGradientColors: theme.mediumContainerGradientColors,
      bigContainerGradientColors: theme.bigContainerGradientColors,
      brightness: "light",
      // Premium only — null clears themeType for regular ready themes
      themeType: isPremiumTheme ? selectedThemeType : null,
    };
  }

  function saveTheme() {
    const confirmSave = window.confirm(
      "Are you sure you want to apply the new theme changes?"
    );
    if (!confirmSave) return;

    const payload = buildThemePayload();

    setLoading(true); // start loader
    dispatch(createThemeRequest(payload));
    toast.success("App Theme Saved Successfully");
    // fallback in case Redux never responds
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  }

  useEffect(() => {
    if (themeState.message) {
      toast.success(themeState.message);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
    if (themeState.error) {
      toast.error(themeState.error);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
  }, [themeState.message, themeState.error, dispatch]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);
  const canCreateTheme = canShowPermission(permissions, "appThemeSelector", "create");
  const editorAccentColor = "#4f46e5";
  const editorAccentGradient = "linear-gradient(135deg, #4f46e5, #6366f1)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        paddingTop: "70px",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      {/* Color selection panel */}
      <div
        style={{
          flex: 2.5,
          overflowY: "auto",
          padding: "24px",
          boxSizing: "border-box",
          borderRight: "1px solid rgba(0,0,0,0.08)",
          background: "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Premium Theme Options */}
        <div
          style={{
            background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            border: "1px solid rgba(251, 191, 36, 0.35)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #F59E0B, #D97706)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: "0 4px 10px rgba(245, 158, 11, 0.35)",
                }}
              >
                <FaCrown size={14} />
              </span>
              <div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#F9FAFB",
                    margin: 0,
                  }}
                >
                  Premium Theme Options
                </h3>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "12px",
                    color: "#D1D5DB",
                  }}
                >
                  Includes icon customization for app icons & accents
                </p>
              </div>
            </div>
            <span style={{ fontSize: "12px", color: "#FCD34D", fontWeight: 600 }}>
              Click any card to apply
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "12px",
            }}
          >
            {premiumThemePresets.map((preset) => {
              const isActive = selectedPresetName === preset.name;
              const IconComponent = preset.icon;
              return (
                <button
                  key={preset.name}
                  onClick={() => applyThemePreset(preset)}
                  style={{
                    border: isActive
                      ? "2px solid #F59E0B"
                      : "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "12px",
                    padding: "12px",
                    background: isActive
                      ? "rgba(245, 158, 11, 0.12)"
                      : "rgba(255,255,255,0.06)",
                    cursor: "pointer",
                    textAlign: "left",
                    boxShadow: isActive
                      ? "0 6px 16px rgba(245,158,11,0.25)"
                      : "0 2px 8px rgba(0,0,0,0.2)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  title={`Apply ${preset.name} theme`}
                >
                  <div
                    style={{
                      height: "56px",
                      borderRadius: "8px",
                      marginBottom: "10px",
                      background: buildScaffoldGradientString(
                        preset.values.scaffoldGradientColors
                      ),
                      border: "1px solid rgba(255,255,255,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        background: preset.iconBg,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: preset.iconColor,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                    >
                      <IconComponent size={16} />
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "6px" }}>
                      {[
                        preset.values.buttonColor,
                        preset.values.appBarColor,
                        preset.values.svgColor,
                        preset.iconBg,
                        preset.values.bottomNavBarSelectedColor,
                      ].map((swatch, index) => (
                        <span
                          key={`${preset.name}-${swatch}-${index}`}
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: swatch,
                            border: "1px solid rgba(255,255,255,0.25)",
                          }}
                        />
                      ))}
                    </div>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#FCD34D",
                        background: "rgba(245, 158, 11, 0.15)",
                        border: "1px solid rgba(245, 158, 11, 0.35)",
                        borderRadius: "999px",
                        padding: "2px 8px",
                      }}
                    >
                      Icons
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#F9FAFB" }}>
                      {preset.name}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px",
                        color: "#D1D5DB",
                      }}
                    >
                      <span
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "6px",
                          background: preset.iconBg,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: preset.iconColor,
                        }}
                      >
                        <IconComponent size={10} />
                      </span>
                      Icon customize
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ready Theme Options */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "14px",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#333",
                margin: 0,
              }}
            >
              Ready Theme Options
            </h3>
            <span style={{ fontSize: "12px", color: "#666" }}>
              Click any card to apply instantly
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "12px",
            }}
          >
            {themePresets.map((preset) => {
              const isActive = selectedPresetName === preset.name;
              return (
                <button
                  key={preset.name}
                  onClick={() => applyThemePreset(preset)}
                  style={{
                    border: isActive
                      ? `2px solid ${editorAccentColor}`
                      : "1px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "10px",
                    background: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    boxShadow: isActive
                      ? "0 6px 16px rgba(79,70,229,0.18)"
                      : "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  title={`Apply ${preset.name} theme`}
                >
                  <div
                    style={{
                      height: "56px",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      background: buildScaffoldGradientString(
                        preset.values.scaffoldGradientColors
                      ),
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    {[
                      preset.values.buttonColor,
                      preset.values.appBarColor,
                      preset.values.appBarTextColor || preset.values.textColor,
                      preset.values.textColor,
                      preset.values.bottomNavBarSelectedColor,
                    ].map((swatch) => (
                      <span
                        key={swatch}
                        style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          background: swatch,
                          border: "1px solid rgba(0,0,0,0.15)",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "#1f2937" }}>
                    {preset.name}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Individual Colors Section - Compact Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {colorConfigs.map(({ key, label, description }) => (
            <div
              key={key}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                border: "2px solid transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                e.currentTarget.style.borderColor = editorAccentColor + "40";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                e.currentTarget.style.borderColor = "transparent";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <input
                  type="color"
                  value={theme[key]}
                  onChange={(e) => handleColorChange(key, e.target.value)}
                  style={{
                    width: "48px",
                    height: "48px",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                      marginBottom: "3px",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#777",
                      marginBottom: "4px",
                    }}
                  >
                    {description}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {theme[key]}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gradient Container Colors Section - Simplified */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#333",
                margin: 0,
              }}
            >
              Gradient Containers
            </h3>
          </div>
          
          {[
            {
              label: "APP Background Color",
              containerKey: "scaffoldGradientColors",
              icon: "🏠",
            },
            {
              label: "Small",
              containerKey: "smallContainerGradientColors",
            
            },
            {
              label: "Medium",
              containerKey: "mediumContainerGradientColors",
           
            },
            {
              label: "Big",
              containerKey: "bigContainerGradientColors",
              icon: "",
            },
          ].map(({ label, containerKey, icon }) => {
            const gradientColors = theme[containerKey] || [];
            const isScaffold = containerKey === "scaffoldGradientColors";
            const gradientFunction = isScaffold ? buildScaffoldGradientString : buildGradientString;
            return (
              <div
                key={label}
                style={{
                  marginBottom: "20px",
                  paddingBottom: "20px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {icon && <span style={{ fontSize: "18px" }}>{icon}</span>}
                    <span style={{ fontWeight: 600, fontSize: "16px", color: "#333" }}>
                      {label} {isScaffold ? "" : "Container"}
                    </span>
                  </div>
                  <button
                    onClick={() => addGradientColor(containerKey)}
                    style={{
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#fff",
                      background: editorAccentGradient,
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    + Add Color
                  </button>
                </div>

                {/* Color Pickers - Compact Horizontal */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  {gradientColors.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#f8f9fa",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = editorAccentColor + "40";
                        e.currentTarget.style.background = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "transparent";
                        e.currentTarget.style.background = "#f8f9fa";
                      }}
                    >
                      <input
                        type="color"
                        value={color}
                        onChange={(e) =>
                          handleGradientColorChange(containerKey, index, e.target.value)
                        }
                        style={{
                          width: "40px",
                          height: "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          fontFamily: "monospace",
                          minWidth: "65px",
                        }}
                      >
                        {color}
                      </div>
                      {gradientColors.length > 2 && (
                        <button
                          onClick={() => removeGradientColor(containerKey, index)}
                          style={{
                            padding: "4px 8px",
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#fff",
                            background: "#ff4444",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#ff0000";
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#ff4444";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title="Remove color"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls Section */}
        <div
          style={{
            padding: "24px",
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            position: "sticky",
            bottom: 16,
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <button
            onClick={goToThemeHistory}
            style={{
              padding: "14px 28px",
              fontSize: "15px",
              fontWeight: 600,
              color: "#333",
              background: "#f0f0f0",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e0e0e0";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f0f0f0";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            View History
          </button>
          {canCreateTheme && (
            <button
              onClick={saveTheme}
              disabled={loading}
              style={{
                padding: "14px 32px",
                fontSize: "15px",
                fontWeight: 600,
                color: "#fff",
                background: editorAccentGradient,
                border: "none",
                borderRadius: "10px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                }
              }}
            >
              {loading ? "Saving..." : "Save Theme"}
            </button>
          )}
        </div>
      </div>

      {/* Preview panel */}
      <div
        style={{
          flex: 1.2,
          padding: "24px",
          overflowY: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "360px",
            height: "700px",
            background: buildScaffoldGradientString(theme.scaffoldGradientColors) || theme.scaffoldBackgroundColor,
            borderRadius: 25,
            border: "8px solid #333",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 15px 40px rgba(0,0,0,0.4)",
            marginTop: "40px",
          }}
        >
          {/* Status Bar */}
          <div
            style={{
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 20px",
              color: theme.scaffoldTextColor,
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <span>1:09</span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>●●●●</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>
          {/* Profile Section / App Bar */}
          <div
            style={{
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: theme.appBarColor,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  background: theme.buttonColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.appBarTextColor,
                  fontWeight: "bold",
                  fontSize: "10px",
                }}
              >
                V
              </div>
              <span
                style={{
                  color: theme.appBarTextColor,
                  fontWeight: 700,
                  fontSize: "12px",
                }}
              >
                Welcome!
              </span>
            </div>
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: `2px solid ${theme.buttonColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.appBarTextColor,
                background: "transparent",
                fontSize: "10px",
              }}
            >
              🔔
            </div>
          </div>
          <HoldingsSection
            containerColor={theme.containerColor}
            containerBorderColor={theme.containerBorderColor}
            valueAmount="504.00"
            cashAmount="0.00"
            textColor={theme.textColor}
            gradientColors={theme.mediumContainerGradientColors}
          />
          <GoldCard
            textcolor={theme.textColor}
            containerColor={theme.containerColor}
            containerBorderColor={theme.containerBorderColor}
            metalName="Gold"
            purity="24K-999"
            pricePerGram="9000.0"
            totalValue="504.00"
            weight="0.056 g"
            metalColor="#FFD700"
            gradientColors={theme.mediumContainerGradientColors}
          />
          {/* Trading Section */}
          <div
            style={{
              background: buildGradientString(theme.bigContainerGradientColors),
              margin: "0 10px",
              borderRadius: "20px",
              padding: "10px",
              border: `1px solid ${theme.containerBorderColor}`,
              opacity: 0.9,
            }}
          >
            {/* Gold Selection */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <button
                style={{
                  flex: 1,
                  padding: "10px",
                  background: theme.buttonColor,
                  border: "none",
                  borderRadius: "25px",
                  flexDirection: "column",
                  color: theme.textColor,
                  fontWeight: "600",
                }}
              >
                Gold 24K-999
              </button>
            </div>

            {/* Input Fields */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: `1px solid ${theme.buttonColor}`,
                  borderRadius: "10px",
                  color: theme.textColor,
                  fontSize: "10px",
                }}
                placeholder="Enter Grams"
              />
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  background: theme.buttonColor,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "center",
                  color: theme.textColor,
                }}
              >
                ⇄
              </div>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: `1px solid ${theme.buttonColor}`,
                  borderRadius: "10px",
                  color: theme.textColor,
                  fontSize: "10px",
                }}
                placeholder="Enter Amount"
              />
            </div>

            {/* Amount Buttons */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
              {["₹ 101", "₹ 501", "₹ 1001", "₹ 5001"].map((amount, index) => {
                const isSelectedAmount = index === 0;
                return (
                <button
                  key={amount}
                  style={{
                    flex: 1,
                    padding: "8px",
                    background: isSelectedAmount
                      ? theme.buttonColor
                      : theme.amountOptionColor,
                    border: "none",
                    borderRadius: "20px",
                    color: "#fff",
                    fontSize: "10px",
                  }}
                >
                  {amount}
                </button>
              );
              })}
            </div>

            {/* Price Info */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  marginBottom: "8px",
                }}
              >
                Current Price (exclusive of 3% gst):
              </div>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                ₹ 9000.0/gm
              </div>
            </div>

            {/* Buy Button */}
            <button
              style={{
                width: "100%",
                padding: "10px",
                background: theme.buttonColor,
                border: "none",
                borderRadius: "25px",
                color: theme.textColor,
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Buy
            </button>
          </div>
          <div>
            <DiamondPointsCard 
              textcolor={theme.textColor} 
              containerColor={theme.containerColor}
              containerBorderColor={theme.containerBorderColor}
              gradientColors={theme.mediumContainerGradientColors}
            />
          </div>
          
          {/* Gradient Container Previews */}
          <div style={{ padding: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {/* Small Container Preview */}
            <div
              style={{
                background: buildGradientString(theme.smallContainerGradientColors),
                borderRadius: "12px",
                padding: "12px",
                textAlign: "center",
                color: theme.textColor,
                fontSize: "11px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              Small Container Gradient
            </div>
            
            {/* Medium Container Preview */}
            <div
              style={{
                background: buildGradientString(theme.mediumContainerGradientColors),
                borderRadius: "15px",
                padding: "18px",
                textAlign: "center",
                color: theme.textColor,
                fontSize: "12px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              Medium Container Gradient
            </div>
            
            {/* Big Container Preview */}
            <div
              style={{
                background: buildGradientString(theme.bigContainerGradientColors),
                borderRadius: "20px",
                padding: "24px",
                textAlign: "center",
                color: theme.textColor,
                fontSize: "13px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              Big Container Gradient
            </div>
          </div>
          {/* Bottom Navigation */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "70px",
              borderRadius: "20px",
              border: `1px solid ${theme.containerBorderColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              background: theme.bottomNavBarBackgroundColor,
              opacity: 0.95,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏠
              </div>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "12px",
                }}
              >
                Home
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                💼
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Savings
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarSelectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🛒
              </div>
              <div
                style={{
                  color: theme.bottomNavBarSelectedColor,
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Buy
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏪
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Store
              </div>
            </div>
            <div style={{ textAlign: "center", position: "relative" }}>
              <div
                style={{
                  color: theme.bottomNavBarUnselectedColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🧭
              </div>
              <div style={{ color: theme.bottomNavBarUnselectedColor, fontSize: "12px" }}>
                Explore
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  right: "8px",
                  width: "8px",
                  height: "8px",
                  background: "#ff4444",
                  borderRadius: "50%",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

